import React, { useRef, useState } from 'react';
import { arrayOf, func, node, shape, string } from 'prop-types';
import { Dropdown } from 'antd';
import './style.scss';

function ThemeXShareMenu({ items, menuId, onVisibleChange, triggerIcon, triggerLabel }) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef(null);
  const menuItemRefs = useRef([]);

  function updateVisibility(nextVisible, { announce = true } = {}) {
    setVisible(nextVisible);
    if (announce) {
      onVisibleChange(nextVisible);
    }

    if (nextVisible) {
      setTimeout(() => {
        if (menuItemRefs.current[0]) {
          menuItemRefs.current[0].focus();
        }
      }, 0);
    }
  }

  function handleMenuKeyDown(event) {
    const menuItems = menuItemRefs.current.filter(Boolean);
    const currentIndex = menuItems.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      updateVisibility(false);
      setTimeout(() => {
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
      }, 0);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex === -1
      ? 0
      : (currentIndex + direction + menuItems.length) % menuItems.length;
    menuItems[nextIndex].focus();
  }

  function handleMenuItemClick(item) {
    item.onClick();
    // The item's own announcement ("Link copied to clipboard!") and the collapse announcement share one
    // live region, and the announcer clears any pending message for that region. Announcing the collapse
    // here would wipe the item's message before a screen reader ever reads it, so stay silent on this path.
    updateVisibility(false, { announce: false });
    // copyToClipboard() and the unmounting menu item both drop focus to <body>; put it back on the trigger
    // so the menu button behaves like the APG menu-button pattern.
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }

  const menu = (
    <ul id={menuId} className="themex-share-menu" role="menu" aria-label="Share options" onKeyDown={handleMenuKeyDown}>
      {items.map((item, index) => (
        <li key={item.key} role="none">
          <button
            type="button"
            className="themex-share-menu-item"
            role="menuitem"
            ref={(element) => {
              menuItemRefs.current[index] = element;
            }}
            onClick={() => handleMenuItemClick(item)}>
            {item.icon}
            {item.label}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="themex-share-menu-wrapper">
      <Dropdown
        overlay={menu}
        trigger={['click']}
        visible={visible}
        overlayClassName="themex-share-dropdown"
        getPopupContainer={(triggerNode) => triggerNode.parentElement}
        onVisibleChange={updateVisibility}>
        <button
          type="button"
          ref={triggerRef}
          className="share-dropdown-trigger"
          aria-haspopup="menu"
          aria-expanded={visible}
          aria-controls={visible ? menuId : undefined}>
          {triggerIcon}
          {triggerLabel}
        </button>
      </Dropdown>
    </div>
  );
}

ThemeXShareMenu.propTypes = {
  items: arrayOf(shape({
    key: string.isRequired,
    label: node.isRequired,
    icon: node,
    onClick: func.isRequired,
  })).isRequired,
  menuId: string.isRequired,
  onVisibleChange: func,
  triggerIcon: node,
  triggerLabel: node.isRequired,
};

ThemeXShareMenu.defaultProps = {
  onVisibleChange: () => {},
  triggerIcon: null,
};

export default ThemeXShareMenu;
